import { Dialog, Notify } from 'quasar'
import CardSearchDialog from 'src/boot/components/CardSearchDialog.vue'
import Card, { BARCODE_URL_DELIMITERS } from 'src/models/card/Card'
import GiftCard from 'src/models/gift-card/GiftCard'
import Model from 'src/models/model'
import Order from 'src/models/order/Order'
import User from 'src/models/user/User'
import IssuedVoucherPackage from 'src/models/voucher/IssuedVoucherPackage'
import Voucher from 'src/models/voucher/Voucher'
import WeakRefSet from 'src/utils/WeakRefSet'

/**
 * @param {import('@quasar/app-webpack').BootFileParams} params
 */
export default async ({ app }) => {
  if (!app.config.globalProperties.$remoteCommands) return

  const remoteCommands = app.config.globalProperties.$remoteCommands
  const cardHandler = {}
  const weakRefSet = new WeakRefSet()

  function trackModel (model) {
    weakRefSet.add(model)
  }

  function teardown () {
    const models = weakRefSet.items()
    for (const model of models) {
      model.destroy()
    }

    weakRefSet.destroy()
  }

  cardHandler.teardown = teardown

  // Utility function to blur input field if focused and return true if it was blurred
  const blurInputFieldIfFocused = () => {
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document?.activeElement?.tagName)) {
      document.activeElement.blur()
      return true
    }
    return false
  }

  remoteCommands.handleCardData = async function (data) {
    if (blurInputFieldIfFocused()) {
      // Small delay to ensure input is fully blurred
      await new Promise(resolve => setTimeout(resolve, 10))
    }

    const sanitizedData = sanitizeQrCodeData(data)

    if (sanitizedData.length > 30) {
      handleLongMagneticCardData(sanitizedData)
    } else {
      handleIdOrQrCodeData(sanitizedData)
    }
  }

  // Expose for web browser based QR Reading
  cardHandler.handleCardData = remoteCommands.handleCardData

  remoteCommands.handleUpcScanned = handleUpcCodeRead

  function sanitizeQrCodeData (data) {
    const urlPathTypes = _.clone(BARCODE_URL_DELIMITERS.ALL)
    let sanitizedData

    while (!sanitizedData && urlPathTypes.length > 0) {
      sanitizedData = data.split(urlPathTypes.shift())[1]
    }

    return sanitizedData || data
  }

  async function handleIdOrQrCodeData (uid, pin = null) {
    if (_.startsWith(uid, 'U')) {
      handleUserQrCodeRead(uid)
      return
    }

    if (_.startsWith(uid, 'VP')) {
      handleVoucherPackageQrCodeRead(uid)
      return
    }

    if (_.startsWith(uid, 'V')) {
      handleVoucherQrCodeRead(uid)
      return
    }

    if (_.startsWith(uid, 'G')) {
      handleGiftCardQrCodeRead(uid)
      return
    }

    if (_.startsWith(uid, 'O')) {
      handleOrderRead(uid)
      return
    }

    // Assume ID Card, look for value or blank
    const idCardInitialReadDismiss = Notify.create({
      message: 'Card read, loading detail ...',
      progress: true,
      type: 'info',
      color: null,
      position: 'top'
    })

    // Check for a matching Card
    const card = new Card({
      options: {
        queryName: 'cardByUid',
        user: {
          authorizedSites: {
            fields: ['id', 'name']
          }
        },
        issuedVoucherPackage: {
          vouchers: {
            voucherType: {
              fields: 'all',
              itemVariation: true
            }
          }
        },
        voucher: {
          voucherType: {
            fields: 'all',
            itemVariation: true
          }
        },
        giftCard: {
          initialValue: 'default',
          balance: 'default',
          card: 'default'
        },
        variables: {
          uid,
          pin
        }
      }
    })
    trackModel(card)
    await card.fetch(true)
    idCardInitialReadDismiss()

    if (cardHandler.anyCardHandler) {
      cardHandler.anyCardHandler(card.isRemote ? card : null)
      return
    }

    if (cardHandler.blankCardHandler) {
      if (card.isBlank) {
        cardHandler.blankCardHandler(uid)
      } else {
        cardHandler.blankCardHandler(false)
      }
      return
    }

    // No specific handlers, do defaults
    if (card.user) {
      console.log('USER FOUND ' + card.user.firstName + ' ' + card.user.lastName)

      if (cardHandler.userCardHandler) {
        cardHandler.userCardHandler(card)
      }
    } else if (card.voucher) {
      voucherRead(card.voucher)
    } else if (card.giftCard) {
      giftCardRead(card.giftCard)
    } else if (card.issuedVoucherPackage) {
      voucherPackageRead(card.issuedVoucherPackage)
    } else {
      Notify.create({
        timeout: 1000,
        message: 'Card not in use',
        type: 'info',
        position: 'top'
      })
    }
  }

  cardHandler.requestBlankCardManualEntry = function (callback) {
    if (cardRequestNotif) {
      // Cancel existing requests
      cardRequestNotif()
    }

    Dialog.create({
      component: CardSearchDialog,
      componentProps: {
        buttonLabel: 'Assign Card'
      }
    }).onOk(async (data) => {
      const { uid, pin } = data
      // Check for a matching Card
      const card = new Card({
        options: {
          queryName: 'cardByUid',
          user: {
            authorizedSites: {
              fields: ['id', 'name']
            }
          },
          issuedVoucherPackage: {
            vouchers: {
              voucherType: {
                fields: 'all',
                itemVariation: true
              }
            }
          },
          voucher: {
            voucherType: {
              fields: 'all',
              itemVariation: true
            }
          },
          giftCard: {
            initialValue: 'default',
            balance: 'default',
            card: 'default'
          },
          variables: {
            uid,
            pin
          }
        }
      })
      await card.fetch(true)
      trackModel(card)
      card.destroy()

      if (card.isRemote) {
        Notify.create({
          timeout: 1000,
          message: 'Card in use',
          type: 'negative',
          position: 'top'
        })
        callback(null)
      } else {
        callback(uid, pin)
      }
    }).onCancel(() => {
      callback(null)
    })
  }

  let cardRequestNotif = null
  cardHandler.requestBlankCardScan = function (label, inUseCardUids = [], callback) {
    if (cardRequestNotif) {
      // Cancel existing requests
      cardRequestNotif()
    }
    let cardRead = false
    let skipPressed = false
    cardHandler.blankCardHandler = function (uid) {
      if (uid === false || _.includes(inUseCardUids, uid)) {
        Notify.create({
          timeout: 1000,
          message: 'Card already used',
          type: 'negative',
          position: 'top'
        })

        cardHandler.requestBlankCardScan(label, inUseCardUids, callback)
        return
      }

      cardRead = true
      cardRequestNotif()
      cardHandler.blankCardHandler = null
      callback(uid)
    }
    cardRequestNotif = Notify.create({
      progress: true,
      timeout: 8000,
      message: 'Tap or swipe to assign card',
      caption: `Card will be associated with ${label}`,
      icon: 'add_box',
      position: 'top',
      actions: [
        { label: 'Skip', handler: () => { skipPressed = true; cardRequestNotif() } }
      ],
      onDismiss () {
        cardHandler.blankCardHandler = null
        if (skipPressed) {
          // eslint-disable-next-line n/no-callback-literal
          callback(false)
        } else if (!cardRead) {
          callback(null)
        }
      }
    })
  }

  cardHandler.requestManualCardEntry = function (callback) {
    if (cardRequestNotif) {
      // Cancel existing requests
      cardRequestNotif()
    }

    Dialog.create({
      component: CardSearchDialog
    }).onOk((data) => {
      cardHandler.anyCardHandler = function (card) {
        callback(card)
        cardHandler.anyCardHandler = null
      }
      // Always expect { uid, pin } format
      handleIdOrQrCodeData(data.uid, data.pin)
    })
  }

  cardHandler.requestCardSearchDialog = function (callback) {
    if (cardRequestNotif) {
      // Cancel existing requests
      cardRequestNotif()
    }

    Dialog.create({
      component: CardSearchDialog,
      componentProps: {
        // Pass any props to your custom component
      }
    }).onOk((data) => {
      // Handle the selected card
      if (data && data.card) {
        callback(data.card)
      } else {
        callback(null)
      }
    }).onCancel(() => {
      callback(null)
    })
  }

  cardHandler.requestCardScan = function (callback) {
    if (cardRequestNotif) {
      // Cancel existing requests
      cardRequestNotif()
    }

    let cardRead = false
    let skipPressed = false
    cardHandler.anyCardHandler = function (card) {
      cardRead = true
      callback(card)
      cardRequestNotif()
      cardHandler.anyCardHandler = null
    }
    cardRequestNotif = Notify.create({
      progress: true,
      timeout: 8000,
      message: 'Tap or swipe to inspect card',
      icon: 'info',
      color: 'info',
      position: 'top',
      actions: [
        { label: 'Cancel', handler: () => { skipPressed = true; cardRequestNotif() } }
      ],
      onDismiss () {
        cardHandler.anyCardHandler = null
        if (skipPressed) {
          // eslint-disable-next-line n/no-callback-literal
          callback(false)
        } else if (!cardRead) {
          callback(null)
        }
      }
    })
  }

  async function handleUserQrCodeRead (uid) {
    const qrUserInitialReadDismiss = Notify.create({
      message: 'Code read, loading detail ...',
      progress: true,
      type: 'info',
      color: null,
      position: 'top'
    })

    // Check for a matching Card
    const user = new User({}, {
      queryName: 'findUserByUid',
      authorizedSites: {
        fields: ['id', 'name']
      },
      variables: { uid }
    })
    trackModel(user)
    await user.fetch(true)
    qrUserInitialReadDismiss()

    if (user && user.isRemote) {
      console.log('USER FOUND ' + user.firstName + ' ' + user.lastName)

      if (cardHandler.userCardHandler) {
        cardHandler.userCardHandler({ user })
      }
    } else {
      Notify.create({
        message: 'User not found',
        type: 'info',
        position: 'top'
      })
    }
  }

  async function handleVoucherQrCodeRead (uid) {
    const voucherInitialReadDismiss = Notify.create({
      message: 'Voucher read, loading detail ...',
      progress: true,
      type: 'info',
      color: null,
      position: 'top'
    })

    const voucher = new Voucher({
      options: {
        queryName: 'voucherByUid',
        voucherType: {
          fields: 'all',
          itemVariation: {
            item: {
              itemVariations: 'default',
              itemModifierSets: {
                defaultModifiers: 'default',
                modifierSet: {
                  modifiers: 'default'
                }
              }
            }
          }
        },
        user: 'default',
        redeemedAt: 'default',
        variables: {
          uid
        }
      }
    })
    trackModel(voucher)
    await voucher.fetch(true)
    voucherInitialReadDismiss()

    voucherRead(voucher)
  }

  async function handleVoucherPackageQrCodeRead (uid) {
    const voucherInitialReadDismiss = Notify.create({
      message: 'Voucher Package read, loading detail ...',
      progress: true,
      type: 'info',
      color: null,
      position: 'top'
    })

    const voucherPackage = new IssuedVoucherPackage({
      options: {
        queryName: 'issuedVoucherPackageByUid',
        vouchers: {
          voucherType: {
            fields: 'all',
            itemVariation: true
          }
        },
        variables: {
          uid
        }
      }
    })
    trackModel(voucherPackage)
    await voucherPackage.fetch(true)
    voucherInitialReadDismiss()

    voucherPackageRead(voucherPackage)
  }

  async function handleGiftCardQrCodeRead (uid) {
    const giftCardInitialReadDismiss = Notify.create({
      message: 'Gift Card read, loading detail ...',
      progress: true,
      type: 'info',
      color: null,
      position: 'top'
    })

    const giftCard = new GiftCard({
      options: {
        queryName: 'giftCardByUid',
        initialValue: 'default',
        balance: 'default',
        variables: {
          uid
        }
      }
    })
    trackModel(giftCard)
    await giftCard.fetch(true)
    giftCardInitialReadDismiss()

    giftCardRead(giftCard)
  }

  async function handleOrderRead (uid) {
    const orderInitialReadDismiss = Notify.create({
      message: 'Order read, loading detail ...',
      progress: true,
      type: 'info',
      color: null,
      position: 'top'
    })

    let id
    if (_.includes(uid, '_')) {
      // Fandango UIDs are prefixed delimited by underscore, example: Oub_6273305
      id = uid.split('_')[1]
    } else {
      // Typical order UIDs are only prefixed by O, example: O6273305
      id = uid.split('O')[1]
    }

    const order = new Order({ id })
    await order.fetch(true)
    orderInitialReadDismiss()

    order.destroy() // Destroy to enable garbage collection

    orderRead(order.id)
  }

  async function handleUpcCodeRead (upcCode) {
    if (blurInputFieldIfFocused()) {
      // Small delay to ensure input is fully blurred
      await new Promise(resolve => setTimeout(resolve, 10))
    }

    const upcInitialReadDismiss = Notify.create({
      message: 'Item scanned ...',
      progress: true,
      type: 'info',
      color: null,
      position: 'top'
    })

    const itemVariation = Model.configuration.itemVariations.find({ upcCode, isActive: true })
    upcInitialReadDismiss()

    if (!itemVariation || !itemVariation.isRemote) {
      Notify.create({
        message: 'UPC code does not match any items',
        type: 'info',
        position: 'top'
      })
    }

    if (cardHandler.upcCodeHandler) {
      cardHandler.upcCodeHandler(itemVariation, upcCode)
    } else {
      Notify.create({
        timeout: 3000,
        message: 'Item scanned but not applied',
        position: 'top'
      })
    }
  }

  function voucherPackageRead (voucherPackage) {
    if (!voucherPackage?.uid) {
      Notify.create({
        message: 'No Voucher Package found',
        type: 'info',
        position: 'top'
      })
      return
    }

    cardHandler.voucherPackageHandler(voucherPackage)
  }

  function voucherRead (voucher) {
    if (!voucher || !voucher.isRemote) {
      notify('No Voucher found', 'info')
    }

    if (voucher.redeemedAt) {
      if (cardHandler.voucherHandler) {
        cardHandler.voucherHandler(voucher, false, 'Voucher has already been redeemed')
      } else {
        notify('Voucher has already been redeemed', 'negative')
      }
    } else if (voucher.user) {
      if (cardHandler.voucherHandler) {
        cardHandler.voucherHandler(voucher, false, 'Voucher already applied to a user')
      } else {
        notify('Voucher already applied to a user', 'negative')
      }
    } else if (voucher.state === 'expired') {
      if (cardHandler.voucherHandler) {
        cardHandler.voucherHandler(voucher, false, 'Voucher has expired')
      } else {
        notify('Voucher has expired', 'negative')
      }
    } else if (voucher.state !== 'valid') {
      if (cardHandler.voucherHandler) {
        cardHandler.voucherHandler(voucher, false, 'Voucher is not valid')
      } else {
        notify('Voucher is not valid', 'negative')
      }
    } else {
      if (cardHandler.voucherHandler) {
        cardHandler.voucherHandler(voucher, true, voucher.voucherType.name)
      } else {
        notify(voucher.voucherType.name + ' voucher applied', 'positive')
      }
    }
  }

  function notify (message, type) {
    Notify.create({
      timeout: 3000,
      message,
      type,
      position: 'top'
    })
  }

  function giftCardRead (giftCard) {
    if (!giftCard || !giftCard.isRemote) {
      Notify.create({
        message: 'No Gift Card found',
        type: 'info',
        position: 'top'
      })
    }

    if (giftCard.redeemedAt) {
      Notify.create({
        timeout: 3000,
        message: 'Gift Card has already been redeemed',
        type: 'negative',
        position: 'top'
      })
    } else if (giftCard.state === 'expired') {
      Notify.create({
        timeout: 3000,
        message: 'Gift Card has expired',
        type: 'negative',
        position: 'top'
      })
    } else if (giftCard.state !== 'valid') {
      Notify.create({
        timeout: 3000,
        message: 'Gift Card is not valid',
        type: 'negative',
        position: 'top'
      })
    } else {
      if (cardHandler.giftCardHandler) {
        cardHandler.giftCardHandler(giftCard)
      } else {
        Notify.create({
          timeout: 3000,
          message: 'Gift Card Scanned, but not applied',
          position: 'top'
        })
      }
    }
  }

  function orderRead (orderId) {
    if (!orderId || orderId < 0) {
      Notify.create({
        message: 'Order not found',
        type: 'info',
        position: 'top'
      })
      return
    }

    if (cardHandler.orderHandler) {
      cardHandler.orderHandler(orderId)
    } else {
      Notify.create({
        timeout: 3000,
        message: 'Order scanned',
        position: 'top'
      })
    }
  }

  async function handleLongMagneticCardData (data) {
    // TODO
    console.log('LongMagneticCardData')
    console.log(data)
  }

  window.$cardHandler = cardHandler
  app.config.globalProperties.$cardHandler = cardHandler

  // Expose the utility function globally
  cardHandler.blurInputFieldIfFocused = blurInputFieldIfFocused
}
